import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';
import type { ExamName } from '../examTasks/examTaskTypes';

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

export type AdminTemplateFile = {
    id: string;
    templateFileId: string;
    examName: ExamName;
    title: string;
    fileName: string;
    contentType: string;
    fileSizeBytes: number;
    fileExtension: string;
    createdAt: string;
    updatedAt: string;
};

export type AdminTemplateListResponse = {
    page: number;
    pageSize: number;
    totalCount: number;
    items: AdminTemplateFile[];
};

export type AdminTemplateUploadInput = {
    examName: ExamName;
    title: string;
    file: File;
};

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct = candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;
    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Admin template request failed'));
    }

    return envelope.data !== undefined ? envelope.data : (payload as T);
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function normalizeExamName(value: unknown): ExamName {
    return value === 'Core' ? 'Core' : 'Academic';
}

function normalizeTemplateFile(value: unknown): AdminTemplateFile {
    const record = asRecord(value);
    const templateFileId = asString(record.templateFileId);

    return {
        id: templateFileId,
        templateFileId,
        examName: normalizeExamName(record.examName),
        title: asString(record.title),
        fileName: asString(record.fileName),
        contentType: asString(record.contentType),
        fileSizeBytes: asNumber(record.fileSizeBytes, 0),
        fileExtension: asString(record.fileExtension),
        createdAt: asString(record.createdAt),
        updatedAt: asString(record.updatedAt),
    };
}

function normalizeTemplateListResponse(value: unknown): AdminTemplateListResponse {
    const payload = unwrapAdminApiPayload(value as AdminTemplateListResponse | AdminApiEnvelope<AdminTemplateListResponse>);
    const record = asRecord(payload);
    const items = Array.isArray(record.items) ? record.items.map(normalizeTemplateFile) : [];

    return {
        page: asNumber(record.page, 1),
        pageSize: asNumber(record.pageSize, items.length || 1),
        totalCount: asNumber(record.totalCount, items.length),
        items,
    };
}

async function requestAdminTemplates<T>(
    path: string,
    init: RequestInit = {},
    query?: Record<string, string>,
): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const resourcePath = trimSlashes(path);
    const url = new URL(resourcePath ? `${apiRoot}/AdminTemplates/${resourcePath}` : `${apiRoot}/AdminTemplates`);

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) {
            throw new Error(getErrorMessage(envelope, 'Admin template request failed'));
        }
    }

    return payload as T;
}

export async function getAdminTemplateList(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    examName?: ExamName | '';
}): Promise<AdminTemplateListResponse> {
    const response = await requestAdminTemplates<unknown>('', { method: 'GET' }, {
        page: String(params?.page ?? 1),
        pageSize: String(params?.pageSize ?? 100),
        search: params?.search?.trim() ?? '',
        examName: params?.examName ?? '',
    });

    return normalizeTemplateListResponse(response);
}

export async function uploadAdminTemplate(input: AdminTemplateUploadInput): Promise<AdminTemplateFile> {
    const formData = new FormData();
    formData.set('ExamName', input.examName);
    formData.set('Title', input.title.trim());
    formData.set('File', input.file);

    const response = await requestAdminTemplates<unknown>('', {
        method: 'POST',
        body: formData,
    });

    return normalizeTemplateFile(unwrapAdminApiPayload(response as AdminTemplateFile | AdminApiEnvelope<AdminTemplateFile>));
}

export async function deleteAdminTemplate(templateFileId: string): Promise<void> {
    await requestAdminTemplates(templateFileId, {
        method: 'DELETE',
    });
}
